import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { searchPrints } from "../../utility/printsApi";
import {
  artworkImageSrcSet,
  artworkImageUrl,
} from "../../utility/artworkImage";
import Button from "../Button";
import DeferredImage from "../DeferredImage";

// Mirrors generateSlug in ArtGallery so print links stay consistent.
const printSlug = (title) =>
  String(title || "print")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");

// "Anything sharing a colour, room, mood or subject with this print, minus
// itself" used to be assembled here as an OR across four Supabase conditions.
// It is one API parameter now — ?similarTo=<id> — which also ranks the row by
// how much each print actually has in common rather than returning whatever
// matched first. See docs/print-search-api.md in create-prints-node.

const PrintCarousel = ({
  title,
  subtitle,
  viewAllUrl,
  viewAllLabel = "Shop all",
  featured = false,
  limit = 12,
  filters = {},
  relatedTo = null,
  // Lets a wider page line the heading up with its own content. The cards
  // themselves stay full bleed either way.
  containerClassName = "max-w-7xl",
}) => {
  const scrollerRef = useRef(null);
  const [prints, setPrints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (window.__CREATE_PRINTS_PRERENDER__) return undefined;

    let cancelled = false;

    const loadPrints = async () => {
      setLoading(true);
      try {
        // The moderation gate lives in the API now, so a carousel cannot
        // accidentally leave it off the way each hand-built query could.
        const { prints: results } = await searchPrints({
          filters,
          similarTo: relatedTo?.id || null,
          featured,
          limit,
        });

        if (cancelled) return;

        setPrints(results.filter((item) => item.image_paths?.[0]));
        setError(false);
        // Scroll anchoring on a long page can leave the row snapped past its
        // left gutter, so a fresh set of prints always starts at the top.
        if (scrollerRef.current) scrollerRef.current.scrollLeft = 0;
      } catch (err) {
        if (!cancelled) setError(true);
        console.error("Error loading carousel prints:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPrints();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featured, limit, JSON.stringify(filters), relatedTo?.id]);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows, prints]);

  const scrollByPage = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: "smooth" });
  };

  return (
    <section className="py-10 sm:py-14">
      <div className={`mx-auto ${containerClassName} px-6`}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl font-bold text-slate-950 sm:text-4xl">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-2 text-base text-slate-600">{subtitle}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="iconSm"
              onClick={() => scrollByPage(-1)}
              disabled={!canScrollLeft}
              aria-label="Scroll left"
              className="hidden border-slate-300 sm:inline-flex"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </Button>
            <Button
              variant="secondary"
              size="iconSm"
              onClick={() => scrollByPage(1)}
              disabled={!canScrollRight}
              aria-label="Scroll right"
              className="hidden border-slate-300 sm:inline-flex"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </Button>
            {viewAllUrl && (
              <Button url={viewAllUrl} variant="secondary" size="sm">
                {viewAllLabel}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="mt-8 flex gap-4 overflow-x-auto scroll-smooth px-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {loading &&
          Array.from({ length: 6 }).map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="w-[44vw] flex-none snap-start sm:w-56 lg:w-64"
            >
              <div className="h-[58vw] w-full animate-pulse rounded-xl bg-slate-200 sm:h-72 lg:h-80" />
              <div className="mt-3 h-4 w-3/4 animate-pulse rounded bg-slate-200" />
            </div>
          ))}

        {!loading &&
          prints.map((print) => (
            <Link
              key={print.id}
              to={`/print/${printSlug(print.title)}/${print.id}`}
              className="group w-[44vw] flex-none snap-start sm:w-56 lg:w-64"
            >
              <div className="h-[58vw] overflow-hidden rounded-xl bg-white sm:h-72 lg:h-80">
                <DeferredImage
                  src={artworkImageUrl(print.image_paths[0], 512)}
                  srcSet={artworkImageSrcSet(
                    print.image_paths[0],
                    [256, 320, 384, 512]
                  )}
                  sizes="(min-width: 1024px) 256px, (min-width: 640px) 224px, 44vw"
                  fallbackSrc={print.image_paths[0]}
                  alt={`${print.title} - Wall Art Print`}
                  className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                />
              </div>
              <p className="mt-3 line-clamp-1 text-sm font-semibold text-slate-950">
                {print.title}
              </p>
              {print.color_category && (
                <p className="mt-0.5 line-clamp-1 text-sm text-slate-600">
                  {print.color_category}
                </p>
              )}
            </Link>
          ))}
      </div>

      {!loading && error && (
        <div className={`mx-auto ${containerClassName} px-6`}>
          <p className="text-sm text-slate-600">
            We couldn't load these prints right now.{" "}
            <Link to="/prints" className="font-semibold underline">
              Browse all prints
            </Link>
          </p>
        </div>
      )}

      {!loading && !error && prints.length === 0 && (
        <div className={`mx-auto ${containerClassName} px-6`}>
          <p className="text-sm text-slate-600">
            No prints here yet.{" "}
            <Link to="/generate-print" className="font-semibold underline">
              Create the first one
            </Link>
          </p>
        </div>
      )}
    </section>
  );
};

export default PrintCarousel;
